import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import BankSelectInput from '@nxsys/components/BankSelectInput';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { countryList } from '@nxsys/utils';
import { Col, Form, Row } from 'antd';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import StepCard from './StepCard';

interface AddBankDetailProps {
  stepNumber: number;
  accountName?: string | null;
  accountNumber?: string | null;
  sortCode?: string | null;
  // Ireland only
  bankName?: string | null;
  buildingSocietyNumber?: string | null;
  IBANReference?: string | null;
  SWIFTNo?: string | null;
  country?: string | null;
  isInternalAccount?: boolean | null;
  completed?: boolean;
  onSubmitStep: (values: any) => void;
}

type AddBankDetailValues = {
  accountName: string;
  accountNumber: string;
  sortCode: string;
  bankName: Option<string> | null;
  buildingSocietyNumber: string;
  IBANReference: string;
  SWIFTNo: string;
  country: Option<string> | null;
};

const AddBankDetail: React.FC<AddBankDetailProps> = ({
  stepNumber,
  accountName,
  accountNumber,
  sortCode,
  bankName,
  buildingSocietyNumber,
  IBANReference,
  SWIFTNo,
  country,
  isInternalAccount: isInternalAccountProp,
  completed = false,
  onSubmitStep,
}) => {
  const [form] = Form.useForm<AddBankDetailValues>();
  const { messages } = useIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  // Ireland accepts either account type, with IBAN required in both cases.
  const [isInternalAccount, setIsInternalAccount] = useState(
    !!isInternalAccountProp,
  );

  const onFinish = (values: AddBankDetailValues) => {
    if (!isIreland) {
      onSubmitStep({
        bankDetails: {
          accountName: values?.accountName || null,
          accountNumber: values?.accountNumber || null,
          sortCode: values?.sortCode || null,
        },
      });
      return;
    }

    // Always submit the required IBAN; keep the other optional fields scoped
    // to the selected account type so an old value is not accidentally cleared.
    onSubmitStep({
      bankDetails: {
        isInternalAccount,
        accountName: values?.accountName || null,
        bankName: values?.bankName?.value || null,
        IBANReference: values?.IBANReference || null,
        ...(isInternalAccount
          ? {
              SWIFTNo: values?.SWIFTNo || null,
              country: values?.country?.value || null,
            }
          : {
              accountNumber: values?.accountNumber || null,
              sortCode: values?.sortCode || null,
              buildingSocietyNumber: values?.buildingSocietyNumber || null,
            }),
      },
    });
  };

  const requiredRule = {
    required: true,
    message: messages['form.pleaseEnter'] as string,
  };

  return (
    <StepCard
      title={messages['prospect.onboarding.step.addBankDetails'] as string}
      stepNumber={stepNumber}
      completed={completed}
      actions={[
        {
          variant: 'outlined',
          name: messages['common.save'] as string,
          onClick: () => form.submit(),
        },
      ]}
    >
      {isIreland && (
        <div className="mb-4 flex items-center gap-2">
          <AppSwitch
            checked={isInternalAccount}
            // The fields of the other account type unmount, so their rules stop
            // applying to the next save; their values stay preserved in the form
            // and onFinish only sends the set that belongs to the account type.
            onChange={setIsInternalAccount}
          />
          <span>
            {(messages['common.internationalAccount'] ??
              'International Account') as string}
          </span>
        </div>
      )}
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        initialValues={{
          accountName,
          accountNumber,
          sortCode,
          bankName: bankName ? { label: bankName, value: bankName } : null,
          buildingSocietyNumber,
          IBANReference,
          SWIFTNo,
          country: country
            ? (countryList.find((option) => option.value === country) ?? null)
            : null,
        }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
      >
        {isIreland && (
          <Form.Item
            label={messages['common.bankName'] as string}
            name="bankName"
          >
            <BankSelectInput
              className="w-full !min-w-0"
              placeholder={messages['form.select'] as string}
            />
          </Form.Item>
        )}

        <Form.Item
          label={messages['common.accountName'] as string}
          name="accountName"
          rules={[
            {
              required: true,
              whitespace: true,
              message: messages['validation.nameRequired'] as string,
            },
          ]}
        >
          <AppInput
            placeholder={
              messages['prospect.onboarding.accountName.placeholder'] as string
            }
          />
        </Form.Item>

        {!isInternalAccount && (
          <>
            <Form.Item
              label={messages['common.accountNumber8Digits'] as string}
              name="accountNumber"
              rules={[
                {
                  required: !isIreland,
                  message: messages['validation.nameRequired'] as string,
                },
                {
                  pattern: /^\d{8}$/,
                  message: messages[
                    'prospect.onboarding.accountNumber.validation'
                  ] as string,
                },
              ]}
              normalize={(value: string) => value.replace(/[^0-9]/g, '')}
            >
              <AppInput
                placeholder={
                  messages[
                    'prospect.onboarding.accountNumber.placeholder'
                  ] as string
                }
                maxLength={8}
              />
            </Form.Item>

            <Form.Item
              label={messages['common.sortCode6Digits'] as string}
              name="sortCode"
              rules={[
                {
                  required: !isIreland,
                  message: messages['validation.nameRequired'] as string,
                },
                {
                  pattern: /^\d{6}$/,
                  message: messages[
                    'prospect.onboarding.sortCode.validation'
                  ] as string,
                },
              ]}
              normalize={(value: string) => value.replace(/[^0-9]/g, '')}
            >
              <AppInput
                placeholder={
                  messages['prospect.onboarding.sortCode.placeholder'] as string
                }
                maxLength={6}
              />
            </Form.Item>

            {isIreland && (
              <Form.Item
                label={
                  messages['common.buildingSocietyNumberUpto24Digits'] as string
                }
                name="buildingSocietyNumber"
                normalize={(value: string) => value.replace(/[^0-9]/g, '')}
              >
                <AppInput
                  placeholder={messages['form.enter'] as string}
                  maxLength={24}
                />
              </Form.Item>
            )}
          </>
        )}

        {isIreland && (
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.IBANReferenceUpto30Digits'] as string}
                name="IBANReference"
                rules={[requiredRule]}
                normalize={(value: string) =>
                  value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
                }
              >
                <AppInput
                  placeholder={messages['form.enter'] as string}
                  maxLength={30}
                />
              </Form.Item>
            </Col>
            {isInternalAccount && (
              <Col xs={24} sm={12}>
                <Form.Item
                  label={messages['common.SWIFTNoUpto20Digits'] as string}
                  name="SWIFTNo"
                  normalize={(value: string) =>
                    value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
                  }
                >
                  <AppInput
                    placeholder={messages['form.enter'] as string}
                    maxLength={20}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        )}

        {isIreland && isInternalAccount && (
          <Form.Item
            label={messages['common.country'] as string}
            name="country"
          >
            <AppSelect
              options={countryList}
              className="w-full !min-w-0"
              placeholder={messages['form.selectCountry'] as string}
            />
          </Form.Item>
        )}
      </AppFormContainer>
    </StepCard>
  );
};

export default AddBankDetail;
